import { MIN_SEARCH_LENGTH } from "@/lib/constants/search";
import { prepareSearchQuery } from "@/lib/search-alias-map";
import {
  gameSearchBlob,
  isExcludedSearchTag,
  textMatchesToken,
  textMatchesWholeWordToken,
} from "@/lib/search-query";
import type { GameWithRelations } from "@/types/game";
import Fuse, { type IFuseOptions } from "fuse.js";

const fuseOptions: IFuseOptions<GameWithRelations> = {
  keys: [
    { name: "name", weight: 0.7 },
    { name: "tags.name", weight: 0.25 },
    { name: "slug", weight: 0.05 },
  ],
  /** Lenient enough for kid-facing names; single-token hits are filtered further below. */
  threshold: 0.42,
  ignoreLocation: true,
  minMatchCharLength: 1,
  includeScore: true,
};

/** Fuse score for alias tag/slug rescues (lower = stronger). */
const ALIAS_MATCH_SCORE = 0.22;
/** Softer score when alias is a related-concept boost (e.g. division → Fractions). */
const RELATED_ALIAS_MATCH_SCORE = 0.28;
/** Score for multi-word token-coverage matches with no Fuse hit. */
const TOKEN_COVERAGE_SCORE = 0.2;
/** Score for strong title/slug substring matches on the raw query. */
const TITLE_SUBSTRING_SCORE = 0.15;

let cachedCatalogRef: GameWithRelations[] | null = null;
let cachedSearchableRef: GameWithRelations[] | null = null;
let cachedFuse: Fuse<GameWithRelations> | null = null;

/** Catalog copy with grade/merchandising tags stripped for Fuse + substring matching. */
function toSearchableCatalog(catalog: GameWithRelations[]): GameWithRelations[] {
  return catalog.map((game) => ({
    ...game,
    tags: (game.tags ?? []).filter((tag) => !isExcludedSearchTag(tag.name)),
  }));
}

function getCatalogFuse(catalog: GameWithRelations[]): {
  searchable: GameWithRelations[];
  fuse: Fuse<GameWithRelations>;
} {
  if (cachedCatalogRef === catalog && cachedFuse && cachedSearchableRef) {
    return { searchable: cachedSearchableRef, fuse: cachedFuse };
  }
  const searchable = toSearchableCatalog(catalog);
  cachedCatalogRef = catalog;
  cachedSearchableRef = searchable;
  cachedFuse = new Fuse(searchable, fuseOptions);
  return { searchable, fuse: cachedFuse };
}

function priorityHas(priorityIds: ReadonlySet<string>, gameId: string | number): boolean {
  return priorityIds.has(String(gameId));
}

/** Strong name match for ordering (exact and substring tiers). */
function nameMatchTier(name: string, term: string): number {
  const n = name.toLowerCase().trim();
  const t = term.toLowerCase().trim();
  if (!t) return 4;
  if (n === t) return 0;
  if (n.startsWith(t)) return 1;
  if (n.includes(t)) return 2;
  return 3;
}

function collectTitleSlugMatches(
  catalog: GameWithRelations[],
  term: string,
  options?: { wholeWordOnly?: boolean }
): GameWithRelations[] {
  const t = term.toLowerCase();
  if (!t) return [];
  const matches = options?.wholeWordOnly ? textMatchesWholeWordToken : textMatchesToken;
  return catalog.filter((game) => {
    const name = (game.name ?? "").toLowerCase();
    if (matches(name, t)) return true;
    const slugWords = (game.slug ?? "").replace(/-/g, " ").toLowerCase();
    return matches(slugWords, t);
  });
}

function collectExactAliasTagMatches(
  catalog: GameWithRelations[],
  tagNames: string[]
): GameWithRelations[] {
  if (tagNames.length === 0) return [];
  const want = new Set(tagNames.map((name) => name.toLowerCase()));
  return catalog.filter((game) =>
    (game.tags ?? []).some((tag) => want.has((tag.name ?? "").toLowerCase()))
  );
}

function collectAliasSlugMatches(
  catalog: GameWithRelations[],
  slugs: string[]
): GameWithRelations[] {
  if (slugs.length === 0) return [];
  const slugSet = new Set(slugs.map((s) => s.toLowerCase()));
  return catalog.filter((game) => slugSet.has((game.slug ?? "").toLowerCase()));
}

function hasExactSearchableTag(game: GameWithRelations, term: string): boolean {
  const t = term.toLowerCase();
  return (game.tags ?? []).some((tag) => {
    const name = (tag.name ?? "").toLowerCase();
    return name === t && !isExcludedSearchTag(tag.name);
  });
}

function tokenCoverage(game: GameWithRelations, tokens: string[]): number {
  if (tokens.length === 0) return 0;
  const blob = gameSearchBlob(game);
  let hits = 0;
  for (const token of tokens) {
    if (textMatchesToken(blob, token)) hits += 1;
  }
  return hits;
}

/** Keep title/slug/exact-tag evidence; drop Fuse-only near-misses like racing→Tracing. */
function isStrongSingleTokenHit(game: GameWithRelations, term: string): boolean {
  if (nameMatchTier(game.name ?? "", term) <= 2) {
    // nameMatchTier uses includes(); require whole-word/prefix for short noisy tokens.
    if (textMatchesToken((game.name ?? "").toLowerCase(), term)) return true;
  }
  const slugWords = (game.slug ?? "").replace(/-/g, " ").toLowerCase();
  if (textMatchesToken(slugWords, term)) return true;
  return hasExactSearchableTag(game, term);
}

type CandidateMeta = {
  game: GameWithRelations;
  fuseScore: number;
  tokenHits: number;
};

function upsertCandidate(
  byId: Map<string, CandidateMeta>,
  game: GameWithRelations,
  fuseScore: number,
  tokenHits: number
): void {
  const id = String(game.id);
  const prev = byId.get(id);
  if (!prev) {
    byId.set(id, { game, fuseScore, tokenHits });
    return;
  }
  byId.set(id, {
    game,
    fuseScore: Math.min(prev.fuseScore, fuseScore),
    tokenHits: Math.max(prev.tokenHits, tokenHits),
  });
}

function compareGamesBySearch(
  a: CandidateMeta,
  b: CandidateMeta,
  term: string,
  tokens: string[],
  priorityIds: ReadonlySet<string>
): number {
  const priorityScoreCutoff = 0.28;
  const tierA = nameMatchTier(a.game.name ?? "", term);
  const tierB = nameMatchTier(b.game.name ?? "", term);
  if (tierA !== tierB) return tierA - tierB;

  // Multi-word: prefer games that cover more query tokens
  if (tokens.length >= 2) {
    const covA = a.tokenHits / tokens.length;
    const covB = b.tokenHits / tokens.length;
    if (covA !== covB) return covB - covA;
  }

  const scoreA = a.fuseScore;
  const scoreB = b.fuseScore;
  const priA = priorityHas(priorityIds, a.game.id) && scoreA < priorityScoreCutoff;
  const priB = priorityHas(priorityIds, b.game.id) && scoreB < priorityScoreCutoff;
  if (priA !== priB) return priA ? -1 : 1;

  if (scoreA !== scoreB) return scoreA - scoreB;

  return (a.game.sort_order ?? 1e9) - (b.game.sort_order ?? 1e9);
}

function addAliasExactMatches(
  byId: Map<string, CandidateMeta>,
  catalog: GameWithRelations[],
  tags: string[],
  slugs: string[],
  tokens: string[],
  aliasScore: number
): void {
  for (const game of collectExactAliasTagMatches(catalog, tags)) {
    upsertCandidate(byId, game, aliasScore, tokenCoverage(game, tokens));
  }
  for (const game of collectAliasSlugMatches(catalog, slugs)) {
    upsertCandidate(byId, game, aliasScore, tokenCoverage(game, tokens));
  }
}

function addAliasWholeWordTitleMatches(
  byId: Map<string, CandidateMeta>,
  catalog: GameWithRelations[],
  term: string,
  tokens: string[]
): void {
  for (const game of collectTitleSlugMatches(catalog, term, { wholeWordOnly: true })) {
    upsertCandidate(byId, game, TITLE_SUBSTRING_SCORE, tokenCoverage(game, tokens));
  }
}

/** Raw-query Fuse + title/slug substring. Never Fuse alias tag names as free text. */
function addRawQueryMatches(
  byId: Map<string, CandidateMeta>,
  searchable: GameWithRelations[],
  fuse: Fuse<GameWithRelations>,
  term: string,
  tokens: string[],
  options: { requireStrongSingleToken: boolean }
): void {
  if (!term) return;

  const fuseResults = fuse.search(term);
  if (fuseResults.length > 0) {
    for (const r of fuseResults) {
      const score = r.score ?? 1;
      if (
        options.requireStrongSingleToken &&
        tokens.length === 1 &&
        !isStrongSingleTokenHit(r.item, term)
      ) {
        continue;
      }
      upsertCandidate(byId, r.item, score, tokenCoverage(r.item, tokens));
    }
  } else {
    for (const game of collectTitleSlugMatches(searchable, term)) {
      upsertCandidate(byId, game, TITLE_SUBSTRING_SCORE, tokenCoverage(game, tokens));
    }
  }
}

function addTokenCoverageMatches(
  byId: Map<string, CandidateMeta>,
  catalog: GameWithRelations[],
  tokens: string[],
  hasAlias: boolean
): void {
  if (tokens.length < 2) return;
  // Unaliased two-word queries must match both tokens (ceil(2*0.5)=1 is too weak —
  // e.g. "zzzz puzzle" / "soccer game" would otherwise surface every Puzzle/"game" hit).
  const minHits = hasAlias ? 1 : Math.max(2, Math.ceil(tokens.length * 0.5));
  for (const game of catalog) {
    const hits = tokenCoverage(game, tokens);
    if (hits >= minHits) {
      upsertCandidate(byId, game, TOKEN_COVERAGE_SCORE, hits);
    }
  }
}

/**
 * Fuzzy search over `catalog` with:
 * normalize → alias exact tags/slugs → optional raw Fuse (title/slug/searchable tags) →
 * token coverage. Related-concept boosts skip raw Fuse to avoid division→Addition leaks.
 */
export function searchAndRankGames(
  catalog: GameWithRelations[],
  term: string,
  priorityIds: ReadonlySet<string>
): GameWithRelations[] {
  const prepared = prepareSearchQuery(term);
  if (prepared.normalized.length < MIN_SEARCH_LENGTH || catalog.length === 0) {
    return [];
  }
  if (prepared.noResults) {
    return [];
  }

  const { searchable, fuse } = getCatalogFuse(catalog);
  const byId = new Map<string, CandidateMeta>();
  const aliasScore = prepared.relatedConceptBoost ? RELATED_ALIAS_MATCH_SCORE : ALIAS_MATCH_SCORE;
  const hasAlias = prepared.alias != null;

  if (hasAlias) {
    addAliasExactMatches(
      byId,
      catalog,
      prepared.alias?.tags ?? [],
      prepared.alias?.slugs ?? [],
      prepared.tokens,
      aliasScore
    );

    // Related boosts (division→Fractions): exact tags/slugs only — no raw-term matching.
    // Other aliases may also hit whole-word title/slug of the full query phrase
    // (not prefixes: car→Card, bus→Buster), and never unconstrained Fuse.
    if (!prepared.relatedConceptBoost) {
      addAliasWholeWordTitleMatches(byId, searchable, prepared.normalized, prepared.tokens);
    }
  } else {
    addRawQueryMatches(byId, searchable, fuse, prepared.normalized, prepared.tokens, {
      requireStrongSingleToken: true,
    });
    addTokenCoverageMatches(byId, searchable, prepared.tokens, false);

    // Fuse/substring can admit single-token fuzzy hits for multi-word queries; enforce the
    // same coverage floor used by token-coverage fallback.
    if (prepared.tokens.length >= 2) {
      const minHits = Math.max(2, Math.ceil(prepared.tokens.length * 0.5));
      for (const [id, meta] of byId) {
        if (meta.tokenHits < minHits) byId.delete(id);
      }
    }
  }

  if (byId.size === 0) {
    return [];
  }

  return [...byId.values()]
    .sort((a, b) => compareGamesBySearch(a, b, prepared.normalized, prepared.tokens, priorityIds))
    .map((c) => c.game);
}
